import { v4 as uuidv4 } from "uuid";
import { sendMessage, getMessageCallBack, setMessageCallBack } from '@/api/wssocket';
import store from '@/store';

/**
 * A2A智能体客户端
 * 基于官方 @a2a-js/sdk 标准实现A2A协议通信
 * 支持标准JSON-RPC 2.0和SSE流式响应
 */
export class A2AClient {
  // 🔥 静态全局事件总线（所有实例共享）
  static globalEventHandlers = new Map();

  static emitGlobalEvent(eventName, data) {
    const handlers = A2AClient.globalEventHandlers.get(eventName) || [];
    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[A2AClient] Global event handler error for ${eventName}:`, error);
      }
    });
  }

  static onGlobalEvent(eventName, handler) {
    if (!A2AClient.globalEventHandlers.has(eventName)) {
      A2AClient.globalEventHandlers.set(eventName, []);
    }
    A2AClient.globalEventHandlers.get(eventName).push(handler);
  }

  static offGlobalEvent(eventName, handler) {
    const handlers = A2AClient.globalEventHandlers.get(eventName) || [];
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  }

  constructor() {
    this.isConnected = false;
    this.currentAgent = null;
    this.messageListeners = [];
    this.statusListeners = [];
    this.serviceEndpoint = null;
    this.agentCard = null;
    this.requestIdCounter = 1;

    // IM桥接相关
    this.imBridgeEnabled = false;
    this.pendingIMRequests = new Map();
    this.originalMessageCallback = null;

    // 终端历史缓存
    this.terminalHistoryCache = new Map();

    // 🔥 事件系统（通用 EventEmitter 模式）
    this.eventHandlers = new Map();

    this.setupIMBridge();
  }

  /**
   * 设置IM桥接
   */
  setupIMBridge() {
    // 保存原始的消息回调
    this.originalMessageCallback = getMessageCallBack();
    
    // 设置新的消息回调，用于处理智能体响应
    const newCallback = (cmd, data) => {
      // 首先调用原始回调
      if (this.originalMessageCallback) {
        this.originalMessageCallback(cmd, data);
      }
      
      // 处理智能体相关的LLM Stream消息
      this.handleIMBridgeMessage(cmd, data);
    };
    
    setMessageCallBack(newCallback);
  }

  /**
   * 处理IM桥接消息
   */
  handleIMBridgeMessage(cmd, data) {
    if (!data || !data.streamId) return;

    const request = this.pendingIMRequests.get(data.streamId);
    if (!request) return;

    switch (cmd) {
      case 11: // LLM_STREAM_CHUNK - 智能体响应数据
        if (request.onData && data.chunk) {
          try {
            const chunkData = JSON.parse(data.chunk);
            // 🔥 将 WebSocket 的 streamId 注入到 chunk 数据中
            chunkData.streamId = data.streamId;
            request.onData(chunkData);

            // 如果是命令输出，触发 terminal-output 事件并缓存
            if (chunkData && chunkData.type === 'command_output' && chunkData.content) {
              const terminalEvent = {
                streamId: data.streamId,
                output: chunkData.content,
                partial: chunkData.partial !== false
              };

              // 发射到当前实例
              this.emit('terminal-output', terminalEvent);

              // 同时通过静态全局事件总线转发
              A2AClient.emitGlobalEvent('terminal-output', terminalEvent);

              // 🔥 缓存纯文本内容（不是 JSON）
              if (data.streamId) {
                const history = this.terminalHistoryCache.get(data.streamId) || '';
                this.terminalHistoryCache.set(data.streamId, history + chunkData.content);

                // 限制缓存大小（保留最近 100KB）
                const updatedHistory = this.terminalHistoryCache.get(data.streamId);
                if (updatedHistory.length > 100000) {
                  this.terminalHistoryCache.set(
                    data.streamId,
                    updatedHistory.slice(-100000)
                  );
                }
              }
            }
          } catch (e) {
            // 🔥 降级处理：如果不是 JSON，作为纯文本处理（向后兼容）
            request.onData(data.chunk);

            // 缓存原始文本
            if (data.streamId && data.chunk) {
              const history = this.terminalHistoryCache.get(data.streamId) || '';
              this.terminalHistoryCache.set(data.streamId, history + data.chunk);

              const updatedHistory = this.terminalHistoryCache.get(data.streamId);
              if (updatedHistory.length > 100000) {
                this.terminalHistoryCache.set(
                  data.streamId,
                  updatedHistory.slice(-100000)
                );
              }
            }
          }
        }
        break;

      case 12: // LLM_STREAM_END - 智能体响应结束
        if (request.onComplete) {
          request.onComplete(request.responseData);
        }
        this.pendingIMRequests.delete(data.streamId);

        // 🔥 触发终端输出结束事件
        this.emit('terminal-output-end', {
          streamId: data.streamId
        });

        // 清理过期缓存（1小时后）
        if (data.streamId) {
          setTimeout(() => {
            this.terminalHistoryCache.delete(data.streamId);
          }, 3600000);
        }
        break;

      case 13: // LLM_STREAM_ERROR - 智能体响应错误
        console.log('[A2AClient] IM Bridge request error:', data);
        if (request.onError) {
          request.onError(new Error(data.error || 'IM Bridge request failed'));
        }
        this.pendingIMRequests.delete(data.streamId);

        // 🔥 触发终端输出错误事件
        this.emit('terminal-output-error', {
          streamId: data.streamId,
          error: data.error || 'IM Bridge request failed'
        });
        break;
    }
  }

  /**
   * 连接到智能体
   * @param {Object} agent 智能体信息
   * @param {Object} options 连接选项
   */
  async connect(agent, options = {}) {
    try {
      console.log('[A2AClient] Connecting to agent:', agent, 'options:', options);
      
      this.currentAgent = agent;
      
      // 检查智能体是否在线
      if (agent.serviceStatus !== 'online' || !agent.isActive) {
        throw new Error('智能体当前不在线');
      }

      // 强制使用IM桥接模式进行测试
      options.forceIMBridge = true;

      // 智能连接策略：直连优先，IM桥接降级
      if (options.forceIMBridge) {
        console.log('[A2AClient] Using forced IM bridge mode');
        this.imBridgeEnabled = true;
        this.serviceEndpoint = null;
      } else if (agent.serviceEndpoint) {
        // 尝试直连模式
        try {
          console.log('[A2AClient] Trying direct connection to:', agent.serviceEndpoint);
          await this.testDirectConnection(agent.serviceEndpoint);
          console.log('[A2AClient] Direct connection successful');
          this.serviceEndpoint = agent.serviceEndpoint;
          this.imBridgeEnabled = false;
        } catch (error) {
          console.warn('[A2AClient] Direct connection failed, falling back to IM bridge:', error);
          this.imBridgeEnabled = true;
          this.serviceEndpoint = null;
        }
      } else {
        console.log('[A2AClient] No service endpoint, using IM bridge mode');
        this.imBridgeEnabled = true;
        this.serviceEndpoint = null;
      }
      
      // 不使用SDK，直接实现A2A协议
      this.officialClient = null;

      // 直接使用智能体信息，不再调用 agent-card.json
      this.agentCard = {
        name: agent.name,
        description: agent.roleDescription,
        endpoints: {
          chat: `${agent.serviceEndpoint}/chat`
        }
      };
      
      this.isConnected = true;
      this.notifyStatusChange('connected');
      
      console.log('[A2AClient] Successfully connected to agent:', agent.id);
      return true;
      
    } catch (error) {
      this.isConnected = false;
      this.notifyStatusChange('error', error.message);
      throw error;
    }
  }

  /**
   * 断开连接
   */
  async disconnect() {
    try {
      this.isConnected = false;
      this.currentAgent = null;
      this.serviceEndpoint = null;
      this.agentCard = null;
      this.imBridgeEnabled = false;
      
      // 清理待处理的IM桥接请求
      this.pendingIMRequests.clear();
      
      // 恢复原始消息回调
      if (this.originalMessageCallback) {
        setMessageCallBack(this.originalMessageCallback);
      }
      
      this.notifyStatusChange('disconnected');
      console.log('[A2AClient] Disconnected from agent');
      
    } catch (error) {
      console.error('[A2AClient] Error during disconnect:', error);
    }
  }
  
  /**
   * 注册消息监听器
   * @param {Function} listener 监听函数
   */
  onMessage(listener) {
    if (typeof listener === 'function') {
      this.messageListeners.push(listener);
    }
  }
  
  /**
   * 注册状态监听器
   * @param {Function} listener 监听函数
   */
  onStatusChange(listener) {
    if (typeof listener === 'function') {
      this.statusListeners.push(listener);
    }
  }
  
  /**
   * 通知消息监听器
   * @param {Object} message 消息对象
   */
  notifyMessage(message) {
    this.messageListeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('[A2AClient] Error in message listener:', error);
      }
    });
  }
  
  /**
   * 通知状态变化
   * @param {string} status 状态
   * @param {string} detail 详情
   */
  notifyStatusChange(status, detail = '') {
    this.statusListeners.forEach(listener => {
      try {
        listener({ status, detail });
      } catch (error) {
        console.error('[A2AClient] Error in status listener:', error);
      }
    });
  }
  
  /**
   * 生成消息ID
   */
  generateMessageId() {
    return uuidv4();
  }
  
  /**
   * 检查智能体是否已连接
   */
  isAgentConnected() {
    return this.isConnected && this.currentAgent !== null;
  }

  /**
   * 测试直连连接
   */
  async testDirectConnection(endpoint) {
    const testEndpoint = endpoint.endsWith('/') ? endpoint + 'execute' : endpoint + '/execute';
    
    try {
      const response = await fetch(testEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          method: "ping",
          params: {}
        }),
        timeout: 5000 // 5秒超时
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return true;
    } catch (error) {
      throw new Error(`Direct connection test failed: ${error.message}`);
    }
  }

  /**
   * 发送消息给智能体 (简化协议，匹配实际智能体支持)
   * @param {string} message 消息内容
   * @param {string} type 消息类型 (text, json)
   * @param {boolean} useStreaming 是否使用流式响应
   */
  async sendMessage(message, type = 'text', useStreaming = true, placeholderId = null) {
    if (!this.isConnected) {
      throw new Error('未连接到智能体');
    }

    // 根据连接模式选择发送方式
    if (this.imBridgeEnabled) {
      return this.sendMessageViaIMBridge(message, type, useStreaming, placeholderId);
    }

    // 优先使用流式响应
    if (useStreaming) {
      return this.sendMessageWithStreaming(message, type, placeholderId);
    }

    try {
      console.log('[A2AClient] Sending message:', { message, type });
      
      // 使用智能体实际支持的简单格式
      const request = {
        method: "execute",
        params: {
          message: message
        }
      };

      const endpoint = await this.getServiceEndpoint();
      
      console.log('[A2AClient] Sending request to:', endpoint, request);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('[A2AClient] Received response:', responseData);
      
      // 处理响应
      let content = '';
      
      if (responseData.success && responseData.result) {
        content = responseData.result;
      } else if (responseData.error) {
        throw new Error(`Agent Error: ${responseData.error}`);
      } else {
        content = '智能体已处理您的请求';
      }
      
      const agentMessage = {
        id: this.generateMessageId(),
        content: content,
        type: 'agent',
        timestamp: Date.now(),
        agentId: this.currentAgent.id,
        agentName: this.currentAgent.name,
        originalResponse: responseData
      };
      
      this.notifyMessage(agentMessage);
      return agentMessage;
      
    } catch (error) {
      console.error('[A2AClient] Failed to send message:', error);
      
      // 根据错误类型提供友好的提示
      let friendlyMessage = '抱歉，出错了！';
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        friendlyMessage = '抱歉，出错了！该智能体暂时无法连接和运行，请联系智能体创作者。';
      } else if (error.message.includes('timeout')) {
        friendlyMessage = '抱歉，智能体响应超时，请稍后再试。';
      } else if (error.message.includes('HTTP 404')) {
        friendlyMessage = '抱歉，智能体服务未找到，请确认智能体是否正常运行。';
      } else if (error.message.includes('HTTP 500') || error.message.includes('HTTP 502')) {
        friendlyMessage = '抱歉，智能体服务暂时出现问题，请稍后再试。';
      } else if (error.message.includes('HTTP')) {
        friendlyMessage = `抱歉，智能体服务返回错误（${error.message}），请稍后再试。`;
      } else {
        friendlyMessage = '抱歉，智能体暂时无法响应，请稍后再试或联系智能体创作者。';
      }
      
      // 创建错误消息
      const errorMessage = {
        id: this.generateMessageId(),
        content: friendlyMessage,
        type: 'error',
        timestamp: Date.now(),
        agentId: this.currentAgent.id,
        agentName: this.currentAgent.name
      };
      
      this.notifyMessage(errorMessage);
      throw error;
    }
  }

  /**
   * 通过IM桥接发送消息
   */
  async sendMessageViaIMBridge(message, type = 'text', useStreaming = true, placeholderId = null) {

    const messageId = this.generateMessageId();
    // 🔥 如果 placeholderId 存在且看起来像 streamId，使用它（用于终端命令等场景）
    const streamId = (placeholderId && placeholderId.startsWith('stream_'))
      ? placeholderId
      : `stream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // 🔥 动态获取终端类型
    const senderTerminal = this.getCurrentTerminal();
    const targetTerminal = this.getTargetTerminal();

    console.log(`[A2AClient] IM桥接发送: ${this.getTerminalName(senderTerminal)}端 → ${this.getTerminalName(targetTerminal)}端, streamId: ${streamId}`);

    // 创建初始消息对象
    const agentMessage = {
      id: messageId,
      content: '',
      placeholderId: placeholderId,
      type: 'agent',
      timestamp: Date.now(),
      agentId: this.currentAgent.id,
      agentName: this.currentAgent.name,
      streaming: useStreaming,
      route: 'im_bridge'
    };
    
    return new Promise((resolve, reject) => {
      let responseData = '';
      let hasCompleted = false;
      // 🔥 移除空闲超时检测：智能体任务执行某些操作时可能长时间无输出
      // 如果真的断线或出错，WebSocket 连接本身会检测到
      // 用户可以手动取消任务，不需要强制超时

      // 注册响应处理器
      const request = {
        onData: (data) => {
          // 🔥 已移除空闲超时检测

          // 🔥 检测是否为 RooCode Agent 结构化消息
          if (data && data.type && typeof data === 'object') {
            // 结构化消息：将原始数据传递给UI，让专门的组件处理
            agentMessage._structuredData = data;
            agentMessage._isStructured = true;

            // 仅用于显示的文本内容（可选）
            if (data.content) {
              responseData += data.content;
              agentMessage.content = responseData;
            }
          } else {
            // 普通文本消息：累积内容
            if (typeof data === 'string') {
              responseData += data;
              agentMessage.content = responseData;
            } else if (typeof data === 'number') {
              // 处理数字类型（JSON.parse 可能会把纯数字字符串解析为数字）
              responseData += String(data);
              agentMessage.content = responseData;
            } else if (data && data.content) {
              responseData += data.content;
              agentMessage.content = responseData;
            } else if (data && typeof data.chunk === 'string') {
              responseData += data.chunk;
              agentMessage.content = responseData;
            }
          }

          // 更新 request 对象中的 responseData
          request.responseData = responseData;

          // 通知UI更新
          this.notifyMessage(agentMessage);
        },
        
        onComplete: (finalData) => {
          if (!hasCompleted) {
            hasCompleted = true;

            agentMessage.streaming = false;
            agentMessage.content = responseData || '智能体已处理您的请求';

            this.notifyMessage(agentMessage);
            resolve(agentMessage);
          }
        },

        onError: (error) => {
          if (!hasCompleted) {
            hasCompleted = true;

            const errorMessage = {
              ...agentMessage,
              content: `❌ 智能体处理失败：${error.message}`,
              type: 'error',
              streaming: false
            };

            this.notifyMessage(errorMessage);
            reject(error);
          }
        }
      };

      // 初始化 responseData 字段
      request.responseData = responseData;

      // 注册 request
      this.pendingIMRequests.set(streamId, request);

      // 发送LLM Stream请求 - 使用完整的IM对聊路由格式
      // 🔥 如果 message 是 JSON 字符串，尝试解析以获取原始类型
      let questionPayload;
      try {
        const parsed = JSON.parse(message);
        // 如果成功解析且包含 type 字段，直接使用（如 terminal_input）
        if (parsed && parsed.type) {
          questionPayload = message; // 保持原始 JSON 字符串
        } else {
          // 否则包装为 text 类型
          questionPayload = JSON.stringify({
            type: 'text',
            content: message,
            timestamp: Date.now()
          });
        }
      } catch (e) {
        // 不是 JSON，包装为 text 类型
        questionPayload = JSON.stringify({
          type: message === '__SAY_HI__' ? 'say_hi' : 'text',
          content: message === '__SAY_HI__' ? '' : message,
          timestamp: Date.now()
        });
      }

      const llmRequest = {
        cmd: 10, // LLM_STREAM_REQUEST
        data: {
          streamId: streamId,
          question: questionPayload,
          sendId: this.getCurrentUserId(), // 当前用户ID作为发送方
          recvId: this.getTargetUserId(), // 目标roo-code用户ID作为接收方
          senderTerminal: senderTerminal, // 🔥 动态发送端终端类型
          targetTerminal: targetTerminal, // 🔥 动态目标端终端类型
          chatType: 'PRIVATE', // 私聊类型
          taskName: `agent_${this.currentAgent.id}`, // 智能体标识
          timestamp: Date.now()
        }
      };
      
      // 仅在开发环境输出详细日志
      if (process.env.NODE_ENV === 'development') {
        console.log('[A2AClient] Sending IM bridge request:', llmRequest);
      }
      
      try {
        sendMessage(llmRequest);
        
        // 如果需要，立即通知消息创建
        if (!agentMessage._notified) {
          agentMessage._notified = true;
          this.notifyMessage(agentMessage);
        }
        
      } catch (error) {
        this.pendingIMRequests.delete(streamId);
        reject(error);
      }
    });
  }

  /**
   * 获取目标用户ID
   */
  getTargetUserId() {
    // 使用智能体的userId（运行智能体的roo-code用户ID），而不是ownerId（拥有者ID）
    return parseInt(this.currentAgent.userId) || 166;
  }

  /**
   * 获取当前运行终端类型
   * @returns {number} 终端类型代码
   */
  getCurrentTerminal() {
    // 优先从store获取（如果可用）
    if (typeof store !== 'undefined' && store.state?.userStore?.userInfo?.terminal !== undefined) {
      const terminal = store.state.userStore.userInfo.terminal;
      console.log('[A2AClient] 从 userStore 获取终端:', terminal, this.getTerminalName(terminal));
      return terminal;
    }

    // 降级：根据环境推断
    if (typeof window !== 'undefined') {
      // 检测VSCode环境（多种方式）
      if (window.vscodeServices ||           // VSCode服务
          window.parent !== window ||        // 在iframe中
          window.acquireVsCodeApi ||         // VSCode WebView API
          window.SharedServicesAccessor ||   // 我们的VSCode服务
          navigator.userAgent.includes('VSCode') || // User Agent
          window.location.protocol === 'vscode-webview:') { // VSCode WebView协议
        console.log('[A2AClient] 降级推断：VSCode环境，使用终端 2（我的电脑）');
        return 2; // VSCode/我的电脑
      }

      // 检测Electron环境
      if (window.require ||
          window.process?.type ||
          window.isElectron ||
          navigator.userAgent.includes('Electron')) {
        console.log('[A2AClient] 降级推断：Electron环境，使用终端 3（我的云电脑）');
        return 3; // PC客户端
      }

      // 检测移动端App环境
      if (window.cordova ||
          window.PhoneGap ||
          window.phonegap ||
          navigator.userAgent.includes('wv') || // WebView
          navigator.userAgent.includes('Mobile')) {
        console.log('[A2AClient] 降级推断：移动App环境，使用终端 1（傻蛋精灵App）');
        return 1; // App
      }
    }

    // 默认Web端
    console.log('[A2AClient] 降级推断：Web环境，使用终端 0（傻蛋网页端）');
    return 0; // Web
  }

  /**
   * 获取目标终端类型（智能体运行的终端）
   * @returns {number} 终端类型代码
   */
  getTargetTerminal() {
    // 智能体通常运行在roo-code（terminal=6）
    // 未来可以从智能体配置中读取
    return this.currentAgent?.terminal || 6;
  }

  /**
   * 获取终端名称（用于日志）
   * @param {number} terminal 终端类型代码
   * @returns {string} 终端名称
   */
  getTerminalName(terminal) {
    const terminalNames = {
      0: "傻蛋网页端",
      1: "傻蛋精灵App",
      2: "我的电脑(VSCode)",
      3: "我的云电脑",
      4: "傻蛋浏览器",
      5: "MCP",
      6: "我的小龙虾"
    };
    return terminalNames[terminal] || `未知终端(${terminal})`;
  }

  /**
   * 发送消息并使用SSE流式响应
   * @param {string} message 消息内容
   * @param {string} type 消息类型 (text, json)
   */
  async sendMessageWithStreaming(message, type = 'text', placeholderId = null) {
    if (!this.isConnected) {
      throw new Error('未连接到智能体');
    }

    // 生成消息ID
    const messageId = this.generateMessageId();
    let streamContent = '';
    let isThinking = false;
    
    try {
      console.log('[A2AClient] Sending streaming message:', { message, type, placeholderId });
      
      const endpoint = await this.getServiceEndpoint();
      
      // 创建初始消息对象，包含占位符ID
      const agentMessage = {
        id: messageId,
        content: '',
        placeholderId: placeholderId,
        type: 'agent',
        timestamp: Date.now(),
        agentId: this.currentAgent.id,
        agentName: this.currentAgent.name,
        streaming: true,
        thinkingContent: '',
        completionContent: ''
      };
      
      // 不要立即通知创建消息，等待第一个事件再创建
      // this.notifyMessage(agentMessage);
      
      // 使用 fetch 发送请求并处理SSE流
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
          method: "execute",
          params: {
            message: message,
            stream: true  // 请求流式响应
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // 检查是否为SSE响应
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('text/event-stream')) {
        // 如果不是SSE，退回到普通响应处理
        const responseData = await response.json();
        agentMessage.content = responseData.result || '智能体已处理您的请求';
        agentMessage.streaming = false;
        this.notifyMessage(agentMessage);
        return agentMessage;
      }

      // 处理SSE流
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      let done = false;
      while (!done) {
        const result = await reader.read();
        done = result.done;
        if (done) break;
        const value = result.value;
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('event:')) {
            const event = line.substring(6).trim();
            console.log('[A2AClient] SSE Event:', event);
          } else if (line.startsWith('data:')) {
            const data = line.substring(5).trim();
            if (data) {
              try {
                const parsedData = JSON.parse(data);
                this.handleStreamEvent(parsedData, agentMessage);
              } catch (e) {
                // 可能是纯文本数据
                this.handleStreamEvent({ type: 'text', content: data }, agentMessage);
              }
            }
          }
        }
      }
      
      // 标记流式结束
      agentMessage.streaming = false;
      this.notifyMessage(agentMessage);
      return agentMessage;
      
    } catch (error) {
      console.error('[A2AClient] Streaming failed:', error);
      
      // 根据错误类型提供友好的提示
      let friendlyMessage = '抱歉，出错了！';
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        friendlyMessage = '抱歉，出错了！该智能体暂时无法连接和运行，请联系智能体创作者。';
      } else if (error.message.includes('timeout')) {
        friendlyMessage = '抱歉，智能体响应超时，请稍后再试。';
      } else if (error.message.includes('HTTP 404')) {
        friendlyMessage = '抱歉，智能体服务未找到，请确认智能体是否正常运行。';
      } else if (error.message.includes('HTTP 500') || error.message.includes('HTTP 502')) {
        friendlyMessage = '抱歉，智能体服务暂时出现问题，请稍后再试。';
      } else if (error.message.includes('HTTP')) {
        friendlyMessage = `抱歉，智能体服务返回错误（${error.message}），请稍后再试。`;
      } else {
        friendlyMessage = '抱歉，智能体暂时无法响应，请稍后再试或联系智能体创作者。';
      }
      
      // 创建错误消息
      const errorMessage = {
        id: this.generateMessageId(),
        content: friendlyMessage,
        type: 'error',
        timestamp: Date.now(),
        agentId: this.currentAgent.id,
        agentName: this.currentAgent.name
      };
      
      this.notifyMessage(errorMessage);
      throw error;
    }
  }

  /**
   * 处理流式事件
   * @param {Object} eventData 事件数据
   * @param {Object} agentMessage 消息对象
   */
  handleStreamEvent(eventData, agentMessage) {
    console.log('[A2AClient] Handling stream event:', eventData);
    
    // 如果是第一次处理事件，且agentMessage还没有被发送过，则先发送一次
    if (!agentMessage._notified) {
      agentMessage._notified = true;
      // 确保第一次通知时内容为空，显示loading状态
      const initialMessage = {
        ...agentMessage,
        content: '', // 强制内容为空
        streaming: true
      };
      this.notifyMessage(initialMessage);
    }
    
    // 首先检查是否是错误事件（多种格式）
    if (eventData.error || eventData.code === 'MESSAGE_ERROR' || eventData.type === 'error') {
      // 错误处理
      agentMessage.type = 'error';
      agentMessage.streaming = false;
      
      // 提取错误信息
      let errorMessage = '';
      if (eventData.error) {
        errorMessage = eventData.error;
      } else if (eventData.details && eventData.details.text) {
        errorMessage = eventData.details.text;
      } else if (eventData.message) {
        errorMessage = eventData.message;
      } else {
        errorMessage = '智能体处理出现未知错误';
      }
      
      // 根据错误类型提供友好的错误消息
      let friendlyMessage = '';
      if (errorMessage.includes('language model did not provide any assistant messages')) {
        friendlyMessage = '🤖 抱歉，智能体暂时无法处理您的请求。这可能是因为：\n\n• 智能体的语言模型配置有问题\n• 您的问题可能超出了智能体的能力范围\n• 智能体服务暂时不稳定\n\n💡 建议：请尝试换个方式提问，或联系智能体创作者进行排查。';
      } else if (errorMessage.includes('timeout') || errorMessage.includes('超时')) {
        friendlyMessage = '⏰ 智能体响应超时，请稍后再试。';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        friendlyMessage = '🌐 网络连接异常，请检查网络后重试。';
      } else if (errorMessage.includes('rate limit') || errorMessage.includes('限制')) {
        friendlyMessage = '🚦 请求频率过高，请稍后再试。';
      } else {
        friendlyMessage = `❌ 智能体处理失败：${errorMessage}\n\n如果问题持续出现，请联系智能体创作者。`;
      }
      
      agentMessage.content = friendlyMessage;
      agentMessage.status = 'error';
      
      // 通知UI更新
      this.notifyMessage(agentMessage);
      return;
    }
    
    // 正常事件处理
    switch (eventData.type) {
      case 'connected':
        // 连接成功
        agentMessage.status = 'connected';
        break;
        
      case 'start':
        // 开始处理
        agentMessage.status = 'processing';
        break;
        
      case 'api_start':
        // API开始处理
        agentMessage.status = 'api_processing';
        // 不要设置内容，保持loading状态
        break;
        
      case 'thinking': {
        // AI思考过程
        if (!agentMessage.thinkingContent) {
          agentMessage.thinkingContent = '';
        }
        // 检查是否是新的内容片段（避免重复）
        const newContent = eventData.content || '';
        if (!agentMessage.thinkingContent.includes(newContent)) {
          agentMessage.thinkingContent += newContent;
        }
        // 更新显示内容
        agentMessage.content = `🤔 思考中...\n${agentMessage.thinkingContent}`;
        break;
      }
        
      case 'completion':
        // AI回复内容
        if (!agentMessage.completionContent) {
          agentMessage.completionContent = '';
        }
        agentMessage.completionContent += eventData.content || '';
        // 只显示回复内容，隐藏思考过程
        agentMessage.content = agentMessage.completionContent;
        break;
        
      case 'done':
        // 处理完成
        agentMessage.status = 'done';
        agentMessage.streaming = false;
        if (eventData.result) {
          agentMessage.content = eventData.result;
        }
        break;
        
      default:
        // 其他事件
        if (eventData.content) {
          agentMessage.content += eventData.content;
        }
        // 移除eventData.message的处理，避免用户问题被错误显示
    }
    
    // 通知UI更新
    this.notifyMessage(agentMessage);
  }


  /**
   * 获取服务端点
   */
  async getServiceEndpoint() {
    let baseEndpoint = this.serviceEndpoint;
    
    if (!baseEndpoint && this.agentCard?.url) {
      baseEndpoint = this.agentCard.url;
    }
    
    if (!baseEndpoint) {
      throw new Error('No service endpoint available');
    }
    
    console.log('[A2AClient] Original baseEndpoint:', baseEndpoint);
    
    // 确保保持原始协议（不要自动升级HTTP到HTTPS）
    let finalEndpoint;
    if (baseEndpoint.endsWith('/')) {
      finalEndpoint = baseEndpoint + 'execute';
    } else {
      finalEndpoint = baseEndpoint + '/execute';
    }
    
    console.log('[A2AClient] Final endpoint before fetch:', finalEndpoint);
    return finalEndpoint;
  }

  /**
   * 查询智能体状态
   */
  async getStatus() {
    if (!this.isConnected) {
      throw new Error('未连接到智能体');
    }

    try {
      // 使用A2A标准JSON-RPC格式查询状态
      const requestId = Date.now();
      const statusRequest = {
        jsonrpc: "2.0",
        id: requestId,
        method: "agent.getStatus",
        params: {}
      };
      
      const endpoint = await this.getServiceEndpoint();
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(statusRequest)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const responseData = await response.json();
      
      if (responseData.error) {
        throw new Error(`A2A Error ${responseData.error.code}: ${responseData.error.message}`);
      }
      
      return responseData.result || responseData;
      
    } catch (error) {
      console.error('[A2AClient] Failed to get status:', error);
      throw error;
    }
  }

  /**
   * 移除监听器
   * @param {Function} listener 监听器函数
   */
  removeListener(listener) {
    this.messageListeners = this.messageListeners.filter(l => l !== listener);
    this.statusListeners = this.statusListeners.filter(l => l !== listener);
  }

  /**
   * 🔥 注册事件监听器（通用 EventEmitter 模式）
   * @param {string} eventName - 事件名称
   * @param {Function} handler - 事件处理函数
   */
  on(eventName, handler) {
    if (!this.eventHandlers.has(eventName)) {
      this.eventHandlers.set(eventName, []);
    }
    this.eventHandlers.get(eventName).push(handler);
    console.log(`[A2AClient] 注册事件监听: ${eventName}, 当前监听器数量: ${this.eventHandlers.get(eventName).length}`);
  }

  /**
   * 🔥 移除事件监听器
   * @param {string} eventName - 事件名称
   * @param {Function} handler - 事件处理函数
   */
  off(eventName, handler) {
    if (!this.eventHandlers.has(eventName)) return;

    const handlers = this.eventHandlers.get(eventName);
    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
      console.log(`[A2AClient] 移除事件监听: ${eventName}, 剩余监听器数量: ${handlers.length}`);
    }

    // 如果没有监听器了，删除事件
    if (handlers.length === 0) {
      this.eventHandlers.delete(eventName);
    }
  }

  /**
   * 🔥 触发事件
   * @param {string} eventName - 事件名称
   * @param {*} data - 事件数据
   */
  emit(eventName, data) {
    if (!this.eventHandlers.has(eventName)) {
      return;
    }

    const handlers = this.eventHandlers.get(eventName);
    console.log(`[A2AClient] 触发事件: ${eventName}, 监听器数量: ${handlers.length}`);

    handlers.forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[A2AClient] 事件处理器错误 (${eventName}):`, error);
      }
    });
  }

  /**
   * 获取当前用户ID
   */
  getCurrentUserId() {
    // 方法1：从导入的store获取
    const storeUserId = store.state.userStore.userInfo.id;
    if (storeUserId && storeUserId !== -1) {
      return storeUserId;
    }

    // 方法2：从localStorage获取（作为备用）
    try {
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo.id && userInfo.id !== -1) {
          return userInfo.id;
        }
      }
    } catch (error) {
      console.error('[A2AClient] Error parsing localStorage userInfo:', error);
    }

    // 使用智能体的ownerId作为当前用户ID（如果可用）
    if (this.currentAgent?.ownerId) {
      return parseInt(this.currentAgent.ownerId);
    }

    // 默认值
    console.warn('[A2AClient] Could not get user ID, using default: 1661');
    return 1661;
  }

  /**
   * 获取当前连接的智能体
   */
  getCurrentAgent() {
    return this.currentAgent;
  }

  /**
   * 测试智能体连接
   * @param {Object} agent 智能体信息
   * @param {Object} options 测试选项
   */
  static async testConnection(agent, options = {}) {
    const testClient = new A2AClient();
    const startTime = Date.now();
    
    try {
      await testClient.connect(agent, options);
      
      // 如果是IM桥接模式，直接返回成功
      if (testClient.imBridgeEnabled) {
        await testClient.disconnect();
        return {
          success: true,
          route: 'im_bridge',
          latency: Date.now() - startTime,
          message: 'IM桥接模式连接成功'
        };
      }
      
      // 直连模式，测试状态
      const status = await testClient.getStatus();
      await testClient.disconnect();
      
      return {
        success: true,
        route: 'direct',
        status: status,
        latency: Date.now() - startTime
      };
      
    } catch (error) {
      await testClient.disconnect();
      
      return {
        success: false,
        route: testClient.imBridgeEnabled ? 'im_bridge' : 'direct',
        error: error.message,
        latency: Date.now() - startTime
      };
    }
  }

  /**
   * 获取连接状态信息
   */
  getConnectionInfo() {
    return {
      isConnected: this.isConnected,
      mode: this.imBridgeEnabled ? 'im_bridge' : 'direct',
      agentId: this.currentAgent?.id,
      agentName: this.currentAgent?.name,
      serviceEndpoint: this.serviceEndpoint
    };
  }

  /**
   * 获取终端历史输出
   * @param {string} streamId - 终端会话ID
   * @returns {string} 历史输出
   */
  getTerminalHistory(streamId) {
    return this.terminalHistoryCache.get(streamId) || '';
  }

  /**
   * 发送 Terminal 命令
   * @param {string} command - 命令内容
   * @param {Object} options - 选项
   * @returns {Promise<string>} streamId
   */
  async sendTerminalCommand(command, options = {}) {
    const streamId = options.streamId || this.generateMessageId();

    const message = {
      type: 'terminal_input',
      action: options.action || 'execute',
      content: command,
      cwd: options.cwd,
      timestamp: Date.now()
    };

    return this.sendMessageViaIMBridge(
      JSON.stringify(message),
      'terminal',
      true,
      streamId
    );
  }
}

// 创建全局实例
export const globalA2AClient = new A2AClient();

// 导出默认实例
export default globalA2AClient;
